import React, { useEffect } from "react";
import { AdyenTransactionStatus, RefundPayload, Transaction } from "../../../Contracts/Transaction";
import { ReactComponent as KebabMenuIcon } from "../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { useTransactionStore } from "../../../Store/Transaction";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../Components/Atoms/Modal";
import Dropdown from "../../../Components/Atoms/Dropdown";
import { toLocalDateString } from "../../../Utils/DateUtils";
import AlertCard from "../../../Components/Atoms/Warning";
import { useTeamStore } from "../../../Store/Team";
import { PaymentRoutes } from "../../../Contracts/Routes";

export interface TransactionListActionProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  transaction: Transaction;
}

const TransactionListActions: React.FC<TransactionListActionProps> = ({
  open,
  transaction,
  onOpen,
  onClose
}) => {
  const { getRefundDetails, refundDetails, doRefund } = useTransactionStore();
  const { getAccessTokenSilently } = useAuth0();
  const [informationDialogue, setInformationDialogue] = React.useState<{
    open: boolean;
    message?: string;
  }>({ open: false });
  const [showRefundModal, setShowRefundModal] = React.useState(false);
  const [refundPayload, setRefundPayload] = React.useState<RefundPayload>({
    refund_reason: ""
  });
  const { formatCurrency } = useTeamStore();
  const handleDropdownChange = (selectedValue: string | number) => {
    if (selectedValue === "refund") {
      setShowRefundModal(true);
    } else if (selectedValue === "refundReceipt") {
      // handleRefundReceipt(); TODO: Implement refund receipt logic
    } else {
      console.warn("Unhandled dropdown value:", selectedValue);
    }
  };

  useEffect(() => {
    getRefundDetails(getAccessTokenSilently, transaction.id);
  }, [transaction.id, getAccessTokenSilently, getRefundDetails]);

  const handleRefundConfirm = async () => {
    const message = await doRefund(getAccessTokenSilently, transaction.id, refundPayload);
    setInformationDialogue({ open: true, message });
    setShowRefundModal(false);
  };
  const postRefundBalance = refundDetails?.available_balance_after_refund ?? 0;
  const isNegative = postRefundBalance < 0;
  const isHardLimit = postRefundBalance <= -10000;
  const isRefundReasonEmpty = refundPayload.refund_reason.trim().length === 0;

  return (
    <div>
      <Modal
        isOpen={informationDialogue.open}
        onClose={() => {
          setInformationDialogue({ open: false });
          onClose();
        }}
        title="Refund Payment"
        size="small"
        onOk={() => {
          setInformationDialogue({ open: false });
          onClose();
        }}
        hideCancelButton={true}
        closable={false}
      >
        <div className="text-wrap">{informationDialogue.message}</div>
      </Modal>

      <Modal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onCancel={() => setShowRefundModal(false)}
        onOk={handleRefundConfirm}
        title="Initiate Refund"
        size="medium"
        okText="Proceed with Refund"
        cancelText="Cancel"
        disableOkButton={isHardLimit || isRefundReasonEmpty}
      >
        <p className="pt-4 w-full border-t border-gray-200 break-words whitespace-pre-wrap">
          You are initiating a refund for a transaction{" "}
          <span className="font-semibold">{transaction.id}</span> associated with client{" "}
          {transaction.customer_id ? (
            <a
              className="text-blue-600  cursor-pointer"
              target="_blank"
              rel="noopener noreferrer"
              href={PaymentRoutes.CUSTOMERS_DETAILS.replace(
                ":id",
                transaction.customer_id.toString()
              )}
            >
              {transaction.customer_name || "Unknown Client"}
            </a>
          ) : (
            <span>{transaction.customer_name}</span>
          )}
        </p>

        <div className="mt-2">
          <label className="block text-large text-gray-800 font-bold mb-1 mt-4">
            Refund Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={refundPayload.refund_reason}
            onChange={e => setRefundPayload({ refund_reason: e.target.value })}
            maxLength={500}
            required
            placeholder="Enter refund reason (max 500 characters)"
            className="w-full mb-2 border border-gray-300 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={1}
          />
          <div className="text-sm text-gray-500 text-right">
            {refundPayload.refund_reason.length}/500 characters
          </div>
        </div>

        <div className="w-full">
          <div className="w-full border-t border-gray-300 py-3">
            <p className="block text-large text-gray-800 font-bold mb-1">Refund Details</p>
            <div className="flex justify-between text-gray-700">
              <span>Refundable Amount:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(refundDetails?.refund_amount ?? 0)}
              </span>
            </div>
            <span className="text-gray-500">(Invoice Amount + Transaction Fee)</span>
          </div>

          <div className="w-full border-t border-gray-300 py-3">
            <p className="block text-large text-gray-800 font-bold mb-1">
              Available Balance After Refund
            </p>
            <div className="flex justify-between text-gray-700 mb-2">
              <span>Current Available Balance:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(refundDetails?.current_balance ?? 0)}{" "}
              </span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>Post Refund Available Balance:</span>
              <span className={`font-medium ${isNegative ? "text-red-600" : "text-gray-900"}`}>
                {formatCurrency(postRefundBalance ?? 0)}
              </span>
            </div>
            {isNegative && (
              <AlertCard
                type={isHardLimit ? "error" : "warning"}
                title={isHardLimit ? "Insufficient Balance" : "Negative Balance Warning"}
                message={
                  isHardLimit
                    ? "Refund cannot be processed as the available balance exceeds the allowable negative limit."
                    : "This refund will result in a negative balance. Zomentum will recover the amount from your next payout. You may proceed with the refund."
                }
              />
            )}
          </div>
        </div>
        <div className="w-full py-3 border-b border-gray-200">
          <div className="flex justify-between text-gray-700 mb-2">
            <span>Payment Method:</span>
            <span className="font-medium text-gray-900">{refundDetails?.payment_method}</span>
          </div>
          <div className="flex justify-between text-gray-700 mb-2">
            <span>Estimated Refund Settlement Date:</span>
            <span className="font-medium text-gray-900">
              {toLocalDateString(refundDetails?.estimated_settlement_date)}
            </span>
          </div>
        </div>
      </Modal>

      <div onClick={() => (open ? onClose() : onOpen())}>
        <Dropdown
          options={[
            {
              label: "Refund Payment",
              value: "refund",
              disabled: transaction.status !== AdyenTransactionStatus.Completed
            },
            {
              label: "Refund Reciept",
              value: "refundReciept",
              disabled: true,
              disabledMessage: "Coming Soon"
            }
          ]}
          position="bottom-right"
          onChange={handleDropdownChange}
          anchor={
            <div className="cursor-pointer flex flex-col items-center">
              <KebabMenuIcon className={`${open ? "text-blue-400" : "text-blue-600"}`} />
              <div className={`${open ? "text-blue-400" : "text-blue-600"}`}>More</div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default TransactionListActions;
